import { useParams, Navigate } from 'react-router-dom';
import { Calendar, Clock, Linkedin } from 'lucide-react';
import { PageHero, SocialShare, LinkedInPrompt } from '@components/common';
import { CALENDLY_URL } from '@constants';
import { getBlogPostBySlug } from '@lib/blog';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPostBySlug(slug) : undefined;

  if (!post) {
    return <Navigate to="/news" replace />;
  }

  const isoDate = `${post.date}T00:00:00+01:00`;
  const socialImage = post.image
    ? `https://thecisonetwork.com${post.image}`
    : 'https://thecisonetwork.com/images/social.png';

  // Article / BlogPosting structured data for SEO
  const blogStructuredData = {
    '@context': 'https://schema.org',
    '@type': ['Article', 'BlogPosting'],
    headline: post.title,
    description: post.excerpt,
    image: socialImage,
    url: `https://thecisonetwork.com/news/${post.slug}`,
    datePublished: isoDate,
    dateModified: isoDate,
    inLanguage: 'en-GB',
    articleSection: post.category,
    author: {
      '@type': 'Person',
      name: post.author.name,
      url: post.author.linkedin,
      image: `https://thecisonetwork.com${post.author.image}`,
      jobTitle: post.author.role,
      worksFor: {
        '@type': 'Organization',
        name: 'The CISO Network',
        url: 'https://thecisonetwork.com',
      },
    },
    publisher: {
      '@type': 'Organization',
      name: 'The CISO Network',
      logo: {
        '@type': 'ImageObject',
        url: 'https://thecisonetwork.com/images/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://thecisonetwork.com/news/${post.slug}`,
    },
  };

  const renderMarkdown = (content: string) => {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];
      const key = i;

      if (line.startsWith('# ')) {
        elements.push(
          <h1 key={key} className="text-3xl font-heading font-bold text-foreground mt-8 mb-4">
            {line.replace('# ', '')}
          </h1>
        );
        i++;
        continue;
      }

      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={key} className="text-2xl font-heading font-bold text-foreground mt-8 mb-4">
            {line.replace('## ', '')}
          </h2>
        );
        i++;
        continue;
      }

      if (line.startsWith('### ')) {
        elements.push(
          <h3 key={key} className="text-xl font-heading font-semibold text-foreground mt-6 mb-3">
            {line.replace('### ', '')}
          </h3>
        );
        i++;
        continue;
      }

      const imageMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
      if (imageMatch) {
        elements.push(
          <img
            key={`img-${key}`}
            src={imageMatch[2]}
            alt={imageMatch[1]}
            className="my-6 rounded-lg max-w-full"
            loading="lazy"
          />
        );
        i++;
        continue;
      }

      if (line.startsWith('- ')) {
        const checkboxMatch = line.match(/^- \[([ x])\] (.*)/);
        if (checkboxMatch) {
          const isChecked = checkboxMatch[1] === 'x';
          const itemText = checkboxMatch[2]
            .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>')
            .replace(
              /\[([^\]]+)\]\(([^)]+)\)/g,
              '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-teal hover:underline">$1</a>'
            );
          elements.push(
            <li key={key} className="text-muted-foreground ml-6 mb-2 flex items-start gap-2">
              <span className="select-none mt-0.5">{isChecked ? '☑' : '☐'}</span>
              <span dangerouslySetInnerHTML={{ __html: itemText }} />
            </li>
          );
        } else {
          const itemContent = line
            .replace('- ', '')
            .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>')
            .replace(
              /\[([^\]]+)\]\(([^)]+)\)/g,
              '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-teal hover:underline">$1</a>'
            );
          elements.push(
            <li
              key={key}
              className="text-muted-foreground ml-6 mb-2"
              dangerouslySetInnerHTML={{ __html: itemContent }}
            />
          );
        }
        i++;
        continue;
      }

      if (/^\d+\. /.test(line)) {
        const itemContent = line
          .replace(/^\d+\. /, '')
          .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>')
          .replace(
            /\[([^\]]+)\]\(([^)]+)\)/g,
            '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-teal hover:underline">$1</a>'
          );
        elements.push(
          <li
            key={key}
            className="text-muted-foreground ml-6 mb-2"
            dangerouslySetInnerHTML={{ __html: itemContent }}
          />
        );
        i++;
        continue;
      }

      if (line.startsWith('|') && line.indexOf('|', 1) > -1) {
        const tableLines: string[] = [];
        while (i < lines.length && lines[i].startsWith('|') && lines[i].indexOf('|', 1) > -1) {
          tableLines.push(lines[i]);
          i++;
        }

        const headerCells = tableLines[0]
          .split('|')
          .filter(Boolean)
          .map(c => c.trim());
        const bodyLines = tableLines.slice(1).filter(l => !l.includes('---'));

        elements.push(
          <div key={`table-wrap-${key}`} className="overflow-x-auto my-6">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  {headerCells.map((cell, idx) => {
                    const html = cell
                      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>')
                      .replace(
                        /\[([^\]]+)\]\(([^)]+)\)/g,
                        '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-teal hover:underline">$1</a>'
                      );
                    return (
                      <th
                        key={idx}
                        className="py-3 px-4 text-left text-sm font-semibold text-foreground whitespace-nowrap"
                        dangerouslySetInnerHTML={{ __html: html }}
                      />
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {bodyLines.map((rowLine, rowIdx) => {
                  const cells = rowLine
                    .split('|')
                    .filter(Boolean)
                    .map(c => c.trim());
                  return (
                    <tr key={rowIdx} className="border-b even:bg-muted/30">
                      {cells.map((cell, cellIdx) => {
                        const html = cell
                          .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>')
                          .replace(
                            /\[([^\]]+)\]\(([^)]+)\)/g,
                            '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-teal hover:underline">$1</a>'
                          );
                        return (
                          <td
                            key={cellIdx}
                            className="py-2 px-4 text-sm text-muted-foreground"
                            dangerouslySetInnerHTML={{ __html: html }}
                          />
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
        continue;
      }

      if (line.startsWith('```')) {
        const codeLines: string[] = [];
        i++;
        while (i < lines.length && !lines[i].startsWith('```')) {
          codeLines.push(lines[i]);
          i++;
        }
        i++; // skip closing ```
        elements.push(
          <pre
            key={`code-${key}`}
            className="bg-muted rounded-lg p-4 overflow-x-auto my-6 text-sm font-mono text-foreground border border-border"
          >
            <code>{codeLines.join('\n')}</code>
          </pre>
        );
        continue;
      }

      if (line.startsWith('> ')) {
        const quoteLines: string[] = [];
        while (i < lines.length && lines[i].startsWith('> ')) {
          quoteLines.push(lines[i].replace(/^> /, ''));
          i++;
        }
        const quoteContent = quoteLines
          .join(' ')
          .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>')
          .replace(/_(.*?)_/g, '<em>$1</em>')
          .replace(
            /`([^`]+)`/g,
            '<code class="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-foreground">$1</code>'
          )
          .replace(
            /\[([^\]]+)\]\(([^)]+)\)/g,
            '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-teal hover:underline">$1</a>'
          );
        elements.push(
          <blockquote
            key={`bq-${key}`}
            className="border-l-4 border-secondary bg-secondary/5 pl-6 py-3 my-6 italic text-muted-foreground rounded-r-lg"
            dangerouslySetInnerHTML={{ __html: quoteContent }}
          />
        );
        continue;
      }

      if (line.trim() === '') {
        elements.push(<div key={key} className="h-4" />);
        i++;
        continue;
      }

      const formatted = line
        .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>')
        .replace(/_(.*?)_/g, '<em>$1</em>')
        .replace(
          /`([^`]+)`/g,
          '<code class="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-foreground">$1</code>'
        )
        .replace(
          /\[([^\]]+)\]\(([^)]+)\)/g,
          '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-teal hover:underline">$1</a>'
        );

      elements.push(
        <p
          key={key}
          className="text-muted-foreground leading-relaxed mb-4"
          dangerouslySetInnerHTML={{ __html: formatted }}
        />
      );
      i++;
    }

    return elements;
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogStructuredData) }}
      />
      <style>{`
        .perspective {
          perspective: 1000px;
        }
        .flip-card {
          perspective: 1000px;
        }
        .flip-card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transition: transform 0.5s;
          transform-style: preserve-3d;
        }
        .group:hover .flip-card-inner {
          transform: rotateY(180deg);
        }
        .flip-card-front,
        .flip-card-back {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          overflow: hidden;
          backface-visibility: hidden;
          box-shadow: 0 0 0 2px white, 0 0 0 4px hsl(180 55% 38%);
        }
        .flip-card-front {
          background-color: #fff;
        }
        .flip-card-back {
          background-color: hsl(180 55% 38%);
          transform: rotateY(180deg);
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
      <PageHero
        title={post.title}
        eyebrow={post.category}
        seoTitle={`${post.title} | The CISO Network`}
        seoDescription={post.excerpt}
        backgroundImage={post.image}
        backgroundAlt={post.title}
        seoImage={socialImage}
      />

      {/* Meta */}
      <section className="bg-primary border-b border-white/10">
        <div className="container-custom py-6">
          <div className="flex items-center gap-4 text-sm text-primary-foreground/60">
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {new Date(post.date).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {post.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <article className="prose prose-lg max-w-none">{renderMarkdown(post.content)}</article>

          {/* Share & Author */}
          <div className="mt-16 pt-8 border-t border-border">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div>
                <p className="text-sm text-muted-foreground mb-4">Share this article</p>
                <SocialShare slug={post.slug} title={post.title} />
              </div>

              <a
                href={post.author.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 group"
              >
                <div className="text-sm text-left sm:text-right">
                  <div className="font-heading font-semibold text-foreground">
                    {post.author.name}
                  </div>
                  <div className="text-muted-foreground">{post.author.role}</div>
                </div>
                <div className="perspective w-10 h-10 flip-card order-first sm:order-none">
                  <div className="flip-card-inner">
                    <div className="flip-card-front">
                      <img
                        src={post.author.image}
                        alt={post.author.name}
                        className="w-full h-full object-cover grayscale"
                      />
                    </div>
                    <div className="flip-card-back">
                      <Linkedin className="h-5 w-5 text-white" />
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      <LinkedInPrompt />

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Seeking Security Insights for Your Business?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Our fractional CISOs can help you implement the strategies discussed in this article.
            Book a call to discuss your security needs.
          </p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
            Book a Call
          </a>
        </div>
      </section>
    </>
  );
}
